/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { updateRecoloredChameleonImagesAction } from 'actions/updateRecoloredChameleonImagesAction';
import ColorPicker from 'components/UI/ColorPicker';
import EditorBtn from 'components/UI/EditorBtn';
import { rawUploader } from 'components/UI/FileUploader';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import { Fragment as _, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import getStore from 'services/getStore';
import { colors } from 'styles/variables';
import { ImageLibraryContext } from '../../EditImage';
import { ChameleonsLibraryContext } from './ChameleonsLibrary';
import { getSVGSize } from 'services/getSVGSize';

const store = getStore();

const update = (property, value) => {
  store.dispatch(
    updateRecoloredChameleonImagesAction({
      [property]: value,
    })
  );
};

const { lGrey0 } = colors;

let defaultColor = '#61CECE';

const SVGDisplay = ({ getRef, svg }) => {
  return (
    <div
      className="fader_-5s"
      css={css`
        padding: 5px;
      `}
    >
      <div ref={ref => getRef(ref)} dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
};

const SVGPreview = ({ src, label }) => {
  const { onAddImage, onClose } = useContext(ImageLibraryContext)!;
  const { cache } = useContext(ChameleonsLibraryContext)!;

  const [svg, setSvg] = useState<any>(cache[src] || null);
  const [changeColorElements, setChangeColorElements] = useState([] as any);

  const fill = useSelector((state: any) => {
    return state.theme.recoloredChameleonImages[src] || defaultColor;
  });

  const mounted = useRef(true);
  const svgDOM = useRef<any>(null);
  const placeholder = useRef<any>(null);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    if (cache[src]) {
      mounted.current && setSvg(cache[src]);
      return;
    }

    // unload it to clear that visual glitch
    if (svg !== null) {
      if (placeholder.current) {
        placeholder.current.style.height = `${
          svgDOM.current.getBoundingClientRect().height
        }px`;
      }
      setSvg('unload');
    }

    //
    fetch(src)
      .then(r => r.text())
      .then(r => {
        if (placeholder.current) {
          placeholder.current.style.height = '0px';
        }
        mounted.current && setSvg(((cache as any)[src] = r));
      });
  }, [src]);

  useEffect(() => {
    if (!svgDOM.current) return;

    svgDOM.current.querySelector('svg');

    const changeColorElements = Array.from(
      svgDOM.current.querySelectorAll('*')
    ).filter((elem: any) => elem.id === 'Change_colour');

    mounted.current && setChangeColorElements(changeColorElements);
  }, [svg]);

  useEffect(() => {
    if (!svgDOM.current) return;

    changeColorElements.forEach((elem: any) => {
      Array.from(elem.querySelectorAll('*')).forEach(
        (childElem: any) => (childElem.style.fill = fill)
      );
    });
  });

  return svg === null ? (
    <LoadingSpinner />
  ) : (
    <_>
      <div
        css={css`
          position: relative;
          width: 100%;
          border: 1px solid #eaefff;
        `}
      >
        {/* <div ref={placeholder} style={{ height: 0 }} /> */}
        {svg !== 'unload' ? (
          <_>
            <SVGDisplay
              key={src}
              getRef={ref => (svgDOM.current = ref)}
              svg={svg}
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                padding: 5px 15px;
                border-top: 1px solid #eaefff;
                font-family: inherit;
                font-size: 12px;
                color: #98add9 !important;
              `}
            >
              {label}
            </div>
          </_>
        ) : (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
            `}
          >
            <LoadingSpinner />
          </div>
        )}
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          margin: 20px 0;
          font-family: inherit;
          font-size: 12px;
        `}
      >
        <ColorPicker
          inColor={fill}
          outColor={color => {
            update(src, color);
          }}
        />
        <div
          css={css`
            margin: 0 0 0 15px;
          `}
        >
          Primary colour
        </div>
      </div>

      <div
        css={css`
          margin: 20px 0;
        `}
      >
        <EditorBtn
          label="Insert image"
          style={{
            ...{
              width: `${'175px'}`,
              margin: '0 0 15px 0',
              border: `1px solid ${lGrey0}`,
              background: '#fff',
              color: lGrey0,
              fontSize: 11,

              hover: {
                color: '#fff',
                background: '#28292b',
              },
            },
          }}
          css={css`
            min-height: 30px;
          `}
          onClick={() => {
            //
            const svg = svgDOM.current.querySelector('svg');
            const bbox = svg.getBBox();
            const { width, height } = bbox;

            const blob = new Blob(
              [new XMLSerializer().serializeToString(svg)],
              { type: 'image/svg+xml;charset=utf-8' }
            );

            const newLabel = label || 'esvg';
            const fileName = `${newLabel}.svg`;

            rawUploader(
              [
                new File([blob], fileName, {
                  type: 'image/svg+xml',
                }),
              ],
              $ => {
                getSVGSize($.url)
                  .then((size: any) => {
                    // console.log({ 'svg size': size });

                    onAddImage!({
                      url: $.url,
                      id: $.id,

                      width: size.width,
                      height: size.height,
                    });
                  })
                  .catch(err => {
                    console.log(err);

                    // some error
                  });

                // onAddImage!({
                //   url: $.url,
                //   id: $.id,
                // });

                onClose!();
              }
            );
          }}
        />
      </div>
    </_>
  );
};

export default SVGPreview;
